import Vue from 'vue';
import Router from 'vue-router';
import store from '@/store';
import { defaultApiErrorHandler, PLATFORM_API } from '@/api.js';

Vue.use(Router);

import { getVueInstanceByName } from '@/utils.js';

//平台管理的视图
import NotFound from '@/modules/common/error/NotFound.vue';
import Forbidden from '@/modules/common/error/Forbidden.vue';
import ServerInternalError from '@/modules/common/error/ServerInternalError.vue';

import Agreement from '@/modules/common/user/Agreement.vue';

import Index from '@/modules/index/Index.vue';

import UpmsUser from '@/modules/platform/upms/UpmsUser.vue';
import UpmsRole from '@/modules/platform/upms/UpmsRole.vue';
import UpmsResource from '@/modules/platform/upms/UpmsResource.vue';
import UpmsOrganization from '@/modules/platform/upms/UpmsOrganization.vue';

import UpmsUserHome from '@/modules/platform/upms/UpmsUserHome.vue';
import UpmsUserSettings from '@/modules/platform/upms/UpmsUserSettings.vue';
import UpmsUserAccessLog from '@/modules/platform/upms/UpmsUserAccessLog.vue';

//VISP平台的视图
import StaffInfo from '@/modules/platform/visp/StaffInfo.vue';
import ServiceItem from '@/modules/platform/visp/ServiceItem.vue';
import ServiceTarget from '@/modules/platform/visp/ServiceTarget.vue';
import MarketTask from '@/modules/platform/visp/MarketTask.vue';
import DbsDistribTask from '@/modules/platform/visp/DbsDistribTask.vue';
import DblDistribTask from '@/modules/platform/visp/DblDistribTask.vue';
import DistribTaskSummary from '@/modules/platform/visp/DistribTaskSummary.vue';
import SalesmanTaskSummary from '@/modules/platform/visp/SalesmanTaskSummary.vue';
import TeamleaderTaskSummary from '@/modules/platform/visp/TeamleaderTaskSummary.vue';
import DistribTaskExcel from '@/modules/platform/visp/DistribTaskExcel.vue';
/**
 * 动态可配置视图集合
 */
const views = { UpmsUser, UpmsRole, UpmsResource, UpmsOrganization, StaffInfo, ServiceItem, ServiceTarget, MarketTask, DbsDistribTask, DblDistribTask, DistribTaskSummary, SalesmanTaskSummary, TeamleaderTaskSummary, DistribTaskExcel };

/**
 * 动态路由前置拦截器
 * 拦截并校验当前登录用户是否具有访问该路由的权限
 * @param {*} to 
 * @param {*} from 
 * @param {*} next 
 */
const dynamicRouteAccessIntercept = (to, from, next) => {
    Vue.axios.get(PLATFORM_API.of('/api/access/permission/assert'), {
            params: {
                url: to.path,
                method: 'GET'
            }
        })
        .then(response => {
            let result = response.data;
            let app = window.app;
            if (result.success) {
                if (result.data) {
                    next();
                } else {
                    next({ path: '/403.html' });
                }
            } else {
                if (app) {
                    app.$message.error(result.message);
                }
            }
        })
        .catch(defaultApiErrorHandler);
};

/**
 * 加载路由
 * @param {*} userNavMenuList 
 */
const loadRouters = (router, userNavMenuList) => {
    let routes = [];
    collectRouters(userNavMenuList, routes);
    routes.push({
        path: '*',
        redirect: '404.html',
    });
    router.addRoutes(routes);
};
const collectRouters = (userNavMenuList, routes) => {
    if (Array.isArray(userNavMenuList)) {
        for (let menu of userNavMenuList) {
            let viewComponent = views[menu.menuViewName];
            if (viewComponent) { //根据视图名称匹配路由视图
                console.log(menu.menuName + ' => ' + menu.menuPath);
                routes.push({
                    path: menu.menuPath,
                    name: menu.menuViewName,
                    component: viewComponent,
                    beforeEnter: dynamicRouteAccessIntercept
                });
            }
            if (Array.isArray(menu.subMenuList) && menu.subMenuList.length) {
                collectRouters(menu.subMenuList, routes);
            }
        }
    }
};

const router = new Router({
    mode: 'history',
    base: process.env.BASE_URL,
    routes: [{
        //一切手动跳转的路由
        path: '/forward',
        name: 'Forward',
        beforeEnter(to, from, next) {
            let viewName = to.params.target;
            next({
                name: viewName
            });
            if (window.app) {
                const app = getVueInstanceByName(window.app.$root, 'AdminApp');
                if (app) { //更新视图(顶部导航菜单及侧边导航菜单的选中状态)
                    app.renderAdminView(viewName, true);
                } else {
                    console.error('>>> No App found with name: AdminApp');
                }
            }
        }
    }, {
        path: '/404.html',
        name: 'NotFound',
        component: NotFound
    }, {
        path: '/403.html',
        name: 'Forbidden',
        component: Forbidden
    }, {
        path: '/500.html',
        name: 'ServerInternalError',
        component: ServerInternalError
    }, {
        path: '/useragreement.html',
        name: 'Agreement',
        component: Agreement
    }, {
        path: '/index.html',
        name: 'Index',
        component: Index
    }, {
        path: '/',
        name: 'Home',
        redirect: '/index.html',
    }, {
        path: '/user/home.html',
        name: 'UpmsUserHome',
        component: UpmsUserHome
    }, {
        path: '/user/settings.html',
        name: 'UpmsUserSettings',
        component: UpmsUserSettings
    }, {
        path: '/user/logs.html',
        name: 'UpmsUserAccessLog',
        component: UpmsUserAccessLog
    }]
});

/**
 * 路由前置拦截器
 */
router.beforeEach((to, from, next) => {
    console.log(`>>> beforeEach, path: ${to.path}`);
    const userAccessState = store.getters.getUserAccessState();
    userAccessState.lastAccessWebUrl = to.path; //记录最近访问的浏览器页面URL
    store.dispatch('executeUpdateUserAccessState', userAccessState); //更新用户访问状态
    if (!store.state.loginUserAllDataReady) { //首次进入页面/或者页面被刷新了?
        Vue.axios
            .get(PLATFORM_API.of('/api/index/all'))
            .then(response => {
                let vue = window.app;
                let result = response.data;
                if (result.success) {
                    let loginUserAllData = result.data;
                    store.dispatch('executeUpdateLoginUserAllData', loginUserAllData);
                    loadRouters(router, loginUserAllData.userNavMenuList); //加载路由
                    next({ path: to.path }); //由于刚刚addRouters仅在下一次路由触发时生效，所以此处得重载该路由,否则404
                } else {
                    vue.$message.error(result.message);
                }
            })
            .catch(defaultApiErrorHandler);
    } else {
        next();
    }
});

router.afterEach((to, from) => {
    console.log(`<<< afterEach, path: ${to.path}`);
});

export { router, views };